import * as R from 'ramda';
import React, { Fragment, useCallback } from 'react';
// components
import { AddDocument2 } from '../../../../components/activities';
import FilePreview from '../../../../components/document-preview/file-preview';
// helpers/constants
import * as G from '../../../../helpers';
import * as GC from '../../../../constants';
// ui
import { Box, Flex, MainActionButton } from '../../../../ui';
// utilities
import { sendRequest } from '../../../../utilities/http';
import endpointsMap from '../../../../utilities/endpoints';
// feature fleet
import GoToConfiguration from './go-to-configuration';
//////////////////////////////////////////////////

export const prepareAndSetMaintenanceDocs = ({
  doc,
  files,
  aiUpload,
  maintenanceDocs,
  setMaintenanceDocs,
  defaultDocumentType,
}) => {
  if (R.and(G.isNilOrEmpty(files), G.isNilOrEmpty(doc))) {
    return setMaintenanceDocs(null);
  }

  if (G.isTrue(aiUpload)) {
    return setMaintenanceDocs(R.prepend(doc, maintenanceDocs));
  }

  const filesWithProps = files.map((file) => ({
    file,
    guid: G.generateGuid(),
    preview: G.createObjectURL(file),
    documentType: defaultDocumentType,
  }));

  const newDocs = R.concat(R.or(maintenanceDocs, []), filesWithProps);

  return setMaintenanceDocs(newDocs);
};

const AiUpload = (props) => {
  const {
    values,
    setValues,
    isEditMode,
    openLoader,
    entityType,
    aiUploadDoc,
    closeLoader,
    setFieldValue,
    leftActiveTab,
    updateDocument,
    setAiUploadDoc,
    getDocumentList,
    maintenanceDocs,
    primaryObjectGuid,
    aiRecognizeRequest,
    setMaintenanceDocs,
    currentUploadedDoc,
    setCurrentUploadedDoc,
    aiUploadDefaultDocumentType,
  } = props;

  const aiUploadGuid = G.getGuidFromObject(aiUploadDoc);
  const filePreview = R.path(['preview'], aiUploadDoc);

  const addDocument = useCallback(async (file) => {
    openLoader();

    const reqFormData = G.makeDataForDocument({
      file,
      primaryObjectGuid,
      documentType: aiUploadDefaultDocumentType,
    });

    const endpoint = R.equals(entityType, 'trailer') ?
      endpointsMap.trailerEquipmentServiceDocument :
      endpointsMap.truckEquipmentServiceDocument;

    const res = await sendRequest('post', endpoint, { data: reqFormData });

    const { status, data } = res;

    if (G.isResponseFail(status)) {
      G.handleFailResponseSimple(res, 'AiUpload -> addDocument fail');
    } else {
      setCurrentUploadedDoc(data);

      getDocumentList();
    }

    closeLoader();
  }, [entityType, primaryObjectGuid, getDocumentList, aiUploadDefaultDocumentType]);

  return (
    <Fragment>
      {
        G.isNilOrEmpty(aiUploadDefaultDocumentType) && <GoToConfiguration entityType={entityType} />
      }
      {
        G.isNilOrEmpty(filePreview) && G.isNotNilAndNotEmpty(aiUploadDefaultDocumentType) &&
        <Flex justifyContent='center' flexDirection='column'>
          <AddDocument2
            m='0px 15px'
            onDropHandler={async (files) => {
              const file = R.head(files || []);

              openLoader();

              const compressedFile = await G.compressFileIfImageTooLarge(file);

              const callback = (data) => {
                const { invoices, serviceId, serviceDate, performedDate } = values;

                const itemIndex = R.lt(leftActiveTab, 3) ? 0 : R.subtract(leftActiveTab, 3);

                const invoice = G.getPropFromObject('invoice', data);
                const dataServiceDate = G.getPropFromObject('serviceDate', data);
                const serviceNumber = G.getPropFromObject('serviceNumber', data);

                const serviceIdToUse = G.isNotNilAndNotEmpty(serviceNumber) ? serviceNumber : serviceId;

                let serviceDateToUse = serviceDate;

                let performedDateToUse = performedDate;

                const currentInvoice = R.path([GC.FIELD_INVOICES, itemIndex], values);

                let newInvoice = G.mergeWithoutNullUndefinedOrEmptyString(currentInvoice, invoice);

                const invoiceDate = G.getPropFromObject(GC.FIELD_INVOICE_DATE, newInvoice);

                if (G.isValidDate(invoiceDate)) {
                  newInvoice = R.assoc(
                    GC.FIELD_INVOICE_DATE,
                    G.convertInstanceToDefaultDateFormat(invoiceDate),
                    newInvoice,
                  );
                }

                if (R.and(G.isNotNilAndNotEmpty(dataServiceDate), G.isValidDate(dataServiceDate))) {
                  serviceDateToUse = G.convertInstanceToDefaultDateFormat(dataServiceDate);

                  performedDateToUse = G.convertInstanceToDefaultDateFormat(dataServiceDate);
                }

                const newInvoices = R.update(itemIndex, newInvoice, invoices);

                const newValues = {
                  ...values,
                  [GC.FIELD_INVOICES]: newInvoices,
                  [GC.FIELD_SERVICE_ID]: serviceIdToUse,
                  [GC.FIELD_SERVICE_DATE]: serviceDateToUse,
                  [GC.FIELD_PERFORMED_DATE]: performedDateToUse,
                };

                setValues(newValues);

                if (G.isTrue(isEditMode)) addDocument(compressedFile);
              };

              aiRecognizeRequest(compressedFile, callback);

              const doc = {
                file: compressedFile,
                guid: G.generateGuid(),
                documentType: aiUploadDefaultDocumentType,
                preview: G.createObjectURL(compressedFile),
              };

              setAiUploadDoc(doc);

              prepareAndSetMaintenanceDocs({
                doc,
                aiUpload: true,
                maintenanceDocs,
                setMaintenanceDocs,
                aiUploadDefaultDocumentType,
              });

              closeLoader();
            }}
          />
        </Flex>
      }
      {
        filePreview &&
        <Flex
          pl={20}
          fontSize={14}
          justifyContent='space-between'
        >
          <MainActionButton
            type='button'
            onClick={() => {
              setAiUploadDoc(null);
              setCurrentUploadedDoc(null);

              const newDocs = R.reject(R.propEq(aiUploadGuid, GC.FIELD_GUID), maintenanceDocs);

              setMaintenanceDocs(newDocs);
            }}
          >
            {G.getWindowLocale('actions:remove-file', 'Remove File')}
          </MainActionButton>
        </Flex>
      }
      { filePreview && (
        <Box p={15} height='calc(100% - 100px)'>
          <FilePreview
            doc={aiUploadDoc}
            updateImage={updateDocument}
            isEditEnabled={G.isNotNilAndNotEmpty(currentUploadedDoc)}
          />
        </Box>
      )}
    </Fragment>
  );
};

export default AiUpload;
